import { type InputCapture } from '@canvas/input';
import type {
  PointerDownCapture,
  PointerMoveCapture,
  PointerUpCapture,
} from '@canvas/input/types/pointer';

import { type SelectionsMachine } from '../../../machine/selection-machine';
import { isSameCell } from '../../../machine/utils/is-same-cell';
import { type ClickTarget } from '../utils/get-click-target';

import { dispatchInitialClick } from './dispatch-initial-click';

export interface MouseState {
  onPointerDown(event: PointerDownCapture): MouseState;
  onPointerMove(event: PointerMoveCapture): MouseState;
  onPointerUp(event: PointerUpCapture): MouseState;
}

export function createMouseStateMachine(
  machine: SelectionsMachine,
  inputCapture: InputCapture,
  getClickTarget: (x: number, y: number) => ClickTarget
): MouseState {
  const idleState: MouseState = {
    // There's no way to get a pointerup or pointermove
    // event in the idle state, so we just fill these with idleState
    onPointerUp: () => idleState,
    onPointerMove: () => idleState,
    onPointerDown: (event) => {
      const { x, y, button } = event.properties;

      // if the button is secondary, that means right-click, which is also context-menu
      // so we ignore it here.
      // this avoids a right click conflicting with a context menu
      if (button === 'secondary') {
        return idleState;
      }

      const target = getClickTarget(x, y);

      if (target.type === 'none') {
        return idleState;
      }

      const state = machine.getState();

      /**
       * If we're editing currently, we may have to ignore, intercept, OR fire a stopEditing dispatch.
       */
      if (state.isEditing) {
        if (
          target.type === 'cell' &&
          state.focusedCell &&
          isSameCell(state.focusedCell, target.position)
        ) {
          // User clicked INSIDE the active editor (e.g., to move the text cursor).
          // We swallow the event completely. Do not dispatch selection events,
          // and strictly return idleState so we don't start dragging.
          return idleState;
        }

        // User clicked ELSEWHERE (a different cell, row header, etc.)
        // We gracefully commit the active edit before we let the selection change.
        machine.dispatch({ type: 'stopEditing', source: 'mouse' });
      }

      const hasShift = inputCapture.hasKeyModifierPressed('Shift');
      const hasMetaOrCtrl =
        inputCapture.hasKeyModifierPressed('Meta') ||
        inputCapture.hasKeyModifierPressed('Control');

      dispatchInitialClick(machine, target, {
        extend: hasShift,
        toggle: hasMetaOrCtrl,
      });

      // if we're in a pointerdown state and we're not shift
      // clicking, we can transition to a dragging state
      if (target.type === 'cell' && !hasShift) {
        return draggingState;
      }

      return idleState;
    },
  };

  const draggingState: MouseState = {
    // If the pointer is down and we continue to get
    // pointerdown events, we ignore them
    onPointerDown: () => draggingState,

    onPointerMove: (event) => {
      const { x, y } = event.properties;
      const target = getClickTarget(x, y);

      // Only extend if we are over a valid cell
      if (target.type === 'cell') {
        machine.dispatch({
          type: 'extendToCell',
          cell: target.position,
          source: 'mouse',
          isDragging: true,
        });
      }

      // Stay Dragging
      return draggingState;
    },

    // if the pointerup event happens while dragging
    // a user has stopped dragging and we transition
    // back to idleState.
    onPointerUp: (event) => {
      // fire the current
      const { x, y } = event.properties;
      const target = getClickTarget(x, y);

      if (target.type === 'cell') {
        machine.dispatch({
          type: 'extendToCell',
          cell: target.position,
          source: 'mouse',
        });
      }

      return idleState;
    },
  };

  return idleState;
}
